/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useEffect, useMemo } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";
import groupBy from "lodash/groupBy";

import {
  GetLicensePlatesByCustomerQuery,
  GetLicensePlatesByCustomerQueryVariables,
  LicensePlate,
  PaginatedLicensePlates,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import useLicensePlateCategoryFromCache from "../../../../../common/hooks/useLicensePlateCategoryFromCache";
import { GET_LICENSE_PLATES_BY_CUSTOMER } from "../../../../../common/operations/queries";
import { errorNotification } from "../../../../../common/variables/notification";
import { useLicensePlatesTableVariables } from "../variables/licensePlatesTable";
import { useGetAllPlateCategoriesCached } from "./useGetAllPlateCategoriesCached";

export interface ILicensePlatesRow extends LicensePlate {
  categoryName: string | null | undefined;
}

export type LicensePlateData = {
  [categoryName: string]: ILicensePlatesRow[];
} | null;

const DEFAULT_LICENCE_PLATES_LIMIT = 300;

export const useGetAllLicensePlatesCached = () => {
  const selectedCustomerId = useCustomerIdGuard();
  const licensePlatesSearchVariable = useLicensePlatesTableVariables();

  const { categories, loading: getCategoriesLoading } =
    useGetAllPlateCategoriesCached();

  const { getCachedCategory } = useLicensePlateCategoryFromCache();

  const [getLicensePlates, { data, fetchMore, loading, networkStatus }] =
    useLazyQuery<
      GetLicensePlatesByCustomerQuery,
      GetLicensePlatesByCustomerQueryVariables
    >(GET_LICENSE_PLATES_BY_CUSTOMER, {
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (!selectedCustomerId && !categories?.items && !categories?.items.length)
      return;

    getLicensePlates({
      variables: {
        input: {
          customerId: selectedCustomerId,
          limit: DEFAULT_LICENCE_PLATES_LIMIT,
        },
      },
    });
  }, [selectedCustomerId, categories?.items, getLicensePlates]);

  useEffect(() => {
    if (!data?.getLicensePlatesByCustomer?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getLicensePlatesByCustomer.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              input: {
                customerId: selectedCustomerId,
                limit: DEFAULT_LICENCE_PLATES_LIMIT,
                nextToken: currentNextToken,
              },
            },
          });

          currentNextToken =
            response.data?.getLicensePlatesByCustomer?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional license plate pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [
    data?.getLicensePlatesByCustomer?.nextToken,
    selectedCustomerId,
    fetchMore,
  ]);

  const licensePlates: LicensePlateData = useMemo(() => {
    if (!data?.getLicensePlatesByCustomer || !categories) {
      return null;
    }

    // response contains all license plates therefore there will not be any empty categories here
    const response = data?.getLicensePlatesByCustomer as PaginatedLicensePlates;
    const filterValue =
      licensePlatesSearchVariable.filterValue.toLocaleLowerCase();

    // add categoryName to licensePlates
    const withCategoryName: ILicensePlatesRow[] = response?.items?.map(
      (item: LicensePlate) => {
        const cache = getCachedCategory(item.categoryId);

        return {
          ...item,
          categoryName: cache?.categoryName ?? "Unknown",
        };
      }
    );

    // filter out license plates
    const filtered =
      withCategoryName.filter((item): boolean => {
        if (filterValue === "") return true;

        return (
          (item.categoryName?.toLowerCase().includes(filterValue) ||
            item.licensePlate.toLowerCase().includes(filterValue) ||
            item.company?.toLowerCase().includes(filterValue) ||
            item.driverName?.toLowerCase().includes(filterValue)) ??
          false
        );
      }) || [];

    let result = groupBy(filtered, "categoryName") as LicensePlateData;

    // start process of adding in missing categories
    const setOfCategories = new Set();

    categories.items.forEach(category => {
      setOfCategories.add(category.categoryName);
    });

    for (const key in result) {
      if (setOfCategories.has(key)) {
        // If the key exists in the Set, remove it
        setOfCategories.delete(key);
      }
    }

    if (result === null) result = {};

    setOfCategories.forEach(categoryName => {
      // typescript thinks result could be null even with above guard
      if (result === null) result = {};

      // add empty category to result
      result[categoryName as string] = [];
    });

    // remove empty string key if it exists
    if (result[""] !== undefined) {
      // note: we should have not let this category be created in DDB
      delete result[""];
    }

    return result;
  }, [
    data?.getLicensePlatesByCustomer,
    categories,
    licensePlatesSearchVariable.filterValue,
  ]);

  const isLoading = loading || getCategoriesLoading;

  const networkStatusLoading = networkStatus === NetworkStatus.fetchMore;

  return {
    licensePlates,
    categories,
    searchValue: licensePlatesSearchVariable.filterValue,
    loading: isLoading || networkStatusLoading,
  };
};
